import React, { useEffect, useState } from 'react';
import { MdAddAPhoto } from 'react-icons/md';
import { toast } from 'react-toastify';
import { FaTimes } from 'react-icons/fa';
import { InternalFieldName, useForm } from 'react-hook-form';
import selectFile from '../../services/fileService';
import { useAuth } from '../../contexts/auth';
import api from '../../services/api';
import CustomModal from '../CustomModal/CustomModal';
import CustomButton from '../CustomButton/CustomButton';

interface EstimateAttachmentsProps {
  estimateId: number;
}

interface EstimateAttachmentModel {
  id: number;
  url?: string;
  name: string;
  thumbnail: string;
}

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function EstimateAttachments({
  estimateId,
}: EstimateAttachmentsProps) {
  const {
    user: { organization },
    isAdmin,
  } = useAuth();

  const {
    setError,
    formState: { errors },
    clearErrors,
  } = useForm<any>({ mode: 'onBlur' });

  const [attachments, setAttachments] = useState<EstimateAttachmentModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(true);
  const [formErrors, setFormErrors] = useState<Record<string, any>>([]);
  const [selectedAttachment, setSelectedAttachment] =
    useState<EstimateAttachmentModel>(
      null as unknown as EstimateAttachmentModel
    );

  async function getFiles() {
    if (organization) {
      setIsLoading(true);
      const response = await api.get(
        `organizations/${organization.id}/estimates/${estimateId}/attachments/pictures`
      );
      setAttachments(response.data.data);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  const sendFile = async (file: File) => {
    try {
      toast.info('Aguarde o upload da foto');
      await api.postForm(
        `organizations/${organization.id}/estimates/${estimateId}/attachments/pictures`,
        { attachment: file }
      );
      toast.success('Foto salva com sucesso');
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422 || e?.response?.status === 400) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  const addFile = async () => {
    const promises: any[] = [];
    clearErrors();
    const files = await selectFile(['image/png', 'image/jpeg'], true);
    if (files && files?.length > 0) {
      files.forEach((file: File) => promises.push(sendFile(file)));
    }
    await Promise.all(promises);
    getFiles();
  };

  const removePhoto = async (attachment: EstimateAttachmentModel) => {
    try {
      if (
        // eslint-disable-next-line no-restricted-globals
        confirm(
          'Você deseja remover o foto da Ordem de Serviço? Essa ação não poderá ser desfeita'
        )
      ) {
        await api.delete(
          `organizations/${organization.id}/estimates/${estimateId}/attachments/pictures/${attachment.id}`
        );

        toast.success('Foto removida com sucesso');
        getFiles();
      }
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  const openPhoto = async (attachmentId: number) => {
    try {
      const { data } = await api.get(
        `organizations/${organization.id}/estimates/${estimateId}/attachments/pictures/${attachmentId}`
      );

      setIsOpenModal(true);
      setSelectedAttachment(data);
    } catch (e: any) {
      toast.error(
        e?.response?.data?.errors.reduce(
          (acc: string, error: string) => acc + error,
          ''
        )
      );
    }
  };

  useEffect(() => {
    if (organization && estimateId) {
      getFiles();
    }
  }, [organization, estimateId]);

  return (
    <>
      <div className='d-flex align-items-center gap-2'>
        {attachments.length > 0 && (
          <div className='overflow-auto'>
            <div className='d-flex'>
              {attachments.map((attachment) => (
                <div className='position-relative m-3' key={attachment.id}>
                  <button
                    style={{ width: '24px' }}
                    className='btn p-0 btn-danger position-absolute top-0 start-0 translate-middle'
                    type='button'
                    onClick={() => removePhoto(attachment)}
                  >
                    <FaTimes />
                  </button>
                  <button
                    className='btn p-0'
                    type='button'
                    onClick={() => openPhoto(attachment.id)}
                  >
                    <img
                      height={80}
                      src={attachment.thumbnail}
                      alt={attachment.name}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <CustomButton
          type='button'
          className='btn text-secondary'
          style={{ border: '1px dashed' }}
          handleClick={() => addFile()}
        >
          <MdAddAPhoto size={40} />
        </CustomButton>
      </div>
      {errors.attachment && (
        <div className='form-text text-danger'>{errors.attachment.message}</div>
      )}
      {selectedAttachment && (
        <CustomModal
          modalTitle={selectedAttachment.name}
          isOpen={isOpenModal}
          setIsOpenModal={setIsOpenModal}
        >
          <div>
            <img
              src={selectedAttachment.url ?? selectedAttachment.thumbnail}
              alt={selectedAttachment.name}
            />
          </div>
        </CustomModal>
      )}
    </>
  );
}
